import { CallbackWrap } from '@/types/common'
import { Embed } from '@/types/embed'
import { LevelingEmbed, LevelingMilestoneGroup } from '@/types/leveling'
import classNames from 'classnames'
import { Input } from 'components/editor/Input'
import React from 'react'
import { useDrag, useDrop } from 'react-dnd'
import scrollIntoView from 'scroll-into-view-if-needed'
import { NumberedMilestone } from './progression-milestone'

let timeout = false

export function findMilestone(
  id: string,
  milestones: Exclude<LevelingEmbed['milestones'], undefined>
) {
  for (let groupIndex = 0; groupIndex < milestones.length; groupIndex++) {
    const group = milestones[groupIndex]
    if (group.id === id) return [undefined, groupIndex, group] as const
    if ('items' in group) {
      for (
        let milestoneIndex = 0;
        milestoneIndex < group.items.length;
        milestoneIndex++
      ) {
        const milestone = group.items[milestoneIndex]
        if (milestone.id === id)
          return [groupIndex, milestoneIndex, milestone] as const
      }
    }
  }

  return [undefined, undefined, undefined] as const
}

export function ProgressionNavItemEditable({
  milestone,
  signalMilestone,
  onEmbedChange
}: {
  milestone: NumberedMilestone
  signalMilestone: (id: string) => void
  onEmbedChange: (value: CallbackWrap<Embed>) => void
}) {
  const updateOrder = React.useCallback(
    (dragId: string, hoverId: string) => {
      if (!onEmbedChange) return
      onEmbedChange((embed) => {
        if (embed.type !== 'leveling' || !embed.milestones) return embed

        const [dragGroupIndex, dragIndex, draggedItem] = findMilestone(
          dragId,
          embed.milestones
        )
        const [hoverGroupIndex, hoverIndex, hoveredItem] = findMilestone(
          hoverId,
          embed.milestones
        )

        if (!draggedItem || !hoveredItem) return embed

        embed = { ...embed }
        embed.milestones = embed.milestones!.slice()
        if (dragGroupIndex === hoverGroupIndex) {
          if (dragGroupIndex === undefined) {
            embed.milestones[dragIndex] = hoveredItem
            embed.milestones[hoverIndex] = draggedItem
          } else {
            const group = {
              ...embed.milestones[dragGroupIndex]
            } as LevelingMilestoneGroup
            group.items = group.items.slice()
            group.items[dragIndex] = hoveredItem
            group.items[hoverIndex] = draggedItem
            embed.milestones[dragGroupIndex] = group
          }
        } else {
          if (hoverGroupIndex !== undefined) {
            const hoverGroup = {
              ...embed.milestones[hoverGroupIndex]
            } as LevelingMilestoneGroup
            hoverGroup.items = hoverGroup.items.slice()
            hoverGroup.items.splice(hoverIndex, 0, draggedItem)
            embed.milestones[hoverGroupIndex] = hoverGroup
          }
          if (dragGroupIndex !== undefined) {
            const dragGroup = {
              ...embed.milestones[dragGroupIndex]
            } as LevelingMilestoneGroup
            dragGroup.items = dragGroup.items.slice()
            dragGroup.items.splice(dragIndex, 1)
            embed.milestones[dragGroupIndex] = dragGroup
          }
          if (hoverGroupIndex === undefined) {
            embed.milestones.splice(hoverIndex, 0, draggedItem)
          }
          if (dragGroupIndex === undefined) {
            embed.milestones.splice(dragIndex, 1)
          }
        }
        return embed
      })
    },
    [onEmbedChange]
  )

  const ref = React.useRef(null)
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'poe-progression-nav-item',
      item: () => {
        return { id: milestone.id, index: milestone.index }
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging()
      })
    }),
    [milestone]
  )

  const [{ handlerId }, drop] = useDrop<
    { id: string },
    undefined,
    { handlerId: string | symbol | null }
  >(
    () => ({
      accept: 'poe-progression-nav-item',
      collect(monitor) {
        return { handlerId: monitor.getHandlerId() }
      },
      hover(dragItem: { id: string }) {
        if (!ref.current || dragItem.id === milestone.id) {
          return
        }

        // This requires a timeout since too many updates can break it
        if (!timeout) {
          timeout = true
          updateOrder(dragItem.id, milestone.id)
          setTimeout(() => {
            timeout = false
          }, 100)
        }
      }
    }),
    [updateOrder, milestone.id]
  )

  drag(drop(ref))

  return (
    <li
      ref={ref}
      className={classNames(
        'poe-milestone-item poe-milestone-item-editing' /*isDragging && 'poe-milestone-item-dragging'*/
      )}
      data-handler-id={handlerId}
      onClick={() => {
        signalMilestone(milestone.id)
        if (typeof document !== 'undefined') {
          const milestoneElem = document.getElementById(
            `milestone-${milestone.id}`
          )
          if (milestoneElem) {
            scrollIntoView(milestoneElem, {
              behavior: 'smooth',
              scrollMode: 'if-needed',
              inline: 'nearest',
              block: 'nearest'
            })
          }
        }
      }}
      onMouseEnter={() => {
        signalMilestone(milestone.id)
      }}>
      <span>
        <span>
          {milestone.index}.
          <Input
            value={milestone.title}
            onChange={(newValue) => {
              onEmbedChange((embed) => {
                if (embed.type !== 'leveling' || !embed.milestones) return embed
                const [groupIndex, index, item] = findMilestone(
                  milestone.id,
                  embed.milestones
                )
                if (!item) return embed
                embed = { ...embed }
                embed.milestones = embed.milestones!.slice()
                if (groupIndex === undefined) {
                  embed.milestones[index] = {
                    ...item,
                    title: newValue
                  }
                } else {
                  const group = {
                    ...embed.milestones[groupIndex]
                  } as LevelingMilestoneGroup
                  group.items = group.items.slice()
                  group.items[index] = {
                    ...item,
                    title: newValue
                  }
                  embed.milestones[groupIndex] = group
                }
                return embed
              })
            }}
          />
        </span>

        <button
          style={{ justifySelf: 'flex-end' }}
          onClick={() => {
            if (
              window.confirm('Are you sure you want to delete this?') === true
            ) {
              onEmbedChange((embed) => {
                if (embed.type !== 'leveling' || !embed.milestones) return embed
                const [groupIndex, index] = findMilestone(
                  milestone.id,
                  embed.milestones
                )
                if (index === undefined) return embed
                embed = { ...embed }
                embed.milestones = embed.milestones!.slice()
                if (groupIndex === undefined) {
                  embed.milestones.splice(index, 1)
                } else {
                  const group = {
                    ...embed.milestones[groupIndex]
                  } as LevelingMilestoneGroup
                  group.items = group.items.slice()
                  group.items.splice(index, 1)
                  embed.milestones[groupIndex] = group
                }
                return embed
              })
            }
          }}>
          Delete
        </button>
      </span>
    </li>
  )
}
